import React, { useEffect, useState } from 'react';
import { TruckIcon, ShieldIcon, LockIcon } from 'lucide-react';
const PRODUCT_IMG = 'https://cdn.magicpatterns.com/uploads/6vSgEsrf1JVpS7x73nKbQH/imgi_33_1765693070-imgi_6_1760553915-227_-_Funnel_Image_(6).png';
function useCountdown(initialSeconds: number) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(s => s <= 1 ? initialSeconds : s - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [initialSeconds]);
  const hh = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mm = String(Math.floor(seconds % 3600 / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return {
    hh,
    mm,
    ss
  };
}
interface PricingCardProps {
  units: string;
  quantity: number;
  price: string;
  originalPrice: string;
  savings: string;
  savingsPct: string;
  perUnit: string;
  perDay: string;
  checkoutUrl: string;
  badge?: string;
  badgeStyle?: string;
  highlighted?: boolean;
  doctorBadge?: boolean;
  popular?: boolean;
}
function BundleImage({
  quantity
}: {
  quantity: number;
}) {
  if (quantity === 1) {
    return <div className="flex justify-center items-end h-32" data-id="element-411">
        <img src={PRODUCT_IMG} alt="Trimora 1 unidad" className="h-28 w-auto object-contain drop-shadow-md" data-id="element-412" />
      </div>;
  }
  if (quantity === 2) {
    return <div className="flex justify-center items-end h-32 gap-1" data-id="element-413">
        <img src={PRODUCT_IMG} alt="Trimora" className="h-24 w-auto object-contain drop-shadow-md -rotate-6 translate-y-1" data-id="element-414" />
        <img src={PRODUCT_IMG} alt="Trimora" className="h-28 w-auto object-contain drop-shadow-md" data-id="element-415" />
      </div>;
  }
  if (quantity === 3) {
    return <div className="flex justify-center items-end h-32 gap-0.5" data-id="element-416">
        <img src={PRODUCT_IMG} alt="Trimora" className="h-22 w-auto object-contain drop-shadow-md -rotate-6 translate-y-2" data-id="element-417" />
        <img src={PRODUCT_IMG} alt="Trimora" className="h-28 w-auto object-contain drop-shadow-md z-10" data-id="element-418" />
        <img src={PRODUCT_IMG} alt="Trimora" className="h-22 w-auto object-contain drop-shadow-md rotate-6 translate-y-2" data-id="element-419" />
      </div>;
  }
  return <div className="flex flex-col items-center gap-0 h-32 justify-end" data-id="element-420">
      <div className="flex justify-center items-end gap-0.5 -mb-2" data-id="element-421">
        <img src={PRODUCT_IMG} alt="Trimora" className="h-16 w-auto object-contain drop-shadow-sm -rotate-6" data-id="element-422" />
        <img src={PRODUCT_IMG} alt="Trimora" className="h-16 w-auto object-contain drop-shadow-sm" data-id="element-423" />
        <img src={PRODUCT_IMG} alt="Trimora" className="h-16 w-auto object-contain drop-shadow-sm rotate-6" data-id="element-424" />
      </div>
      <div className="flex justify-center items-end gap-0.5" data-id="element-425">
        <img src={PRODUCT_IMG} alt="Trimora" className="h-20 w-auto object-contain drop-shadow-md -rotate-3" data-id="element-426" />
        <img src={PRODUCT_IMG} alt="Trimora" className="h-24 w-auto object-contain drop-shadow-md z-10" data-id="element-427" />
        <img src={PRODUCT_IMG} alt="Trimora" className="h-20 w-auto object-contain drop-shadow-md rotate-3" data-id="element-428" />
      </div>
    </div>;
}
function PricingCard({
  units,
  quantity,
  price,
  originalPrice,
  savings,
  savingsPct,
  perUnit,
  perDay,
  checkoutUrl,
  badge,
  badgeStyle,
  highlighted,
  doctorBadge,
  popular
}: PricingCardProps) {
  return <div className={`bg-card rounded-2xl p-6 flex flex-col gap-4 relative transition-all duration-200 ${highlighted ? 'border-2 border-green-500 shadow-xl ring-2 ring-green-500 ring-offset-2' : 'border border-border shadow-sm hover:shadow-md'}`} data-id="element-429">
      {badge && <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5" data-id="element-430">
          <span className={`px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap shadow-sm ${badgeStyle}`} data-id="element-431">
            {badge}
          </span>
          {doctorBadge && <span className="px-2.5 py-0.5 text-[10px] font-semibold rounded-full whitespace-nowrap bg-green-100 text-green-800 border border-green-200" data-id="element-432">
              RECOMENDADO POR MÉDICOS
            </span>}
        </div>}

      {/* Savings % pill */}
      <div className={`flex justify-center ${badge ? 'pt-6' : 'pt-2'}`} data-id="element-433">
        <span className="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full border border-red-200" data-id="element-434">
          Ahorras {savingsPct} — {savings}
        </span>
      </div>

      <BundleImage quantity={quantity} data-id="element-435" />

      <div className="text-center" data-id="element-436">
        <h3 className="text-xl font-bold text-foreground" data-id="element-437">{units}</h3>
        <div className="mt-1 flex items-center justify-center gap-2" data-id="element-438">
          <span className="text-sm text-muted-foreground line-through" data-id="element-439">
            {originalPrice}
          </span>
          <span className="text-xs bg-red-100 text-red-700 font-bold px-1.5 py-0.5 rounded" data-id="element-440">
            -{savingsPct}
          </span>
        </div>
        <div className="mt-0.5" data-id="element-441">
          <span className="text-4xl font-bold text-foreground" data-id="element-442">{price}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5" data-id="element-443">{perUnit}</p>
        <p className="text-xs font-semibold text-green-700 mt-1" data-id="element-444">
          Solo {perDay}/día — menos que un café ☕
        </p>
      </div>

      <div className="flex flex-col gap-2" data-id="element-445">
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-446">
          <TruckIcon className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-447" />
          <span data-id="element-448">Envío gratis hoy</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-449">
          <ShieldIcon className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-450" />
          <span data-id="element-451">Garantía 90 días</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-452">
          <LockIcon className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-453" />
          <span data-id="element-454">Pago 100% seguro</span>
        </div>
      </div>

      <a href={checkoutUrl} className={`w-full font-bold py-3 px-6 rounded-xl transition-colors duration-200 text-sm tracking-wide shadow-sm hover:shadow-md text-center block ${highlighted ? 'bg-green-600 hover:bg-green-700 text-white text-base py-4' : 'bg-green-600 hover:bg-green-700 text-white'}`} data-id="element-455">
        {highlighted ? 'EMPEZAR MI TRANSFORMACIÓN →' : 'PEDIR AHORA'}
      </a>

      {popular && <p className="text-center text-xs font-semibold text-green-700" data-id="element-456">
          ⭐ Recomendado por el 78% de clientas
        </p>}
      <p className="text-center text-xs text-muted-foreground" data-id="element-457">
        Compra única · Sin suscripción
      </p>
    </div>;
}
export function PricingSection() {
  const {
    hh,
    mm,
    ss
  } = useCountdown(10 * 3600 + 16 * 60 + 13);
  return <section className="bg-muted py-16 sm:py-24" data-id="element-458">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-459">
        <div className="text-center mb-10" data-id="element-460">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 text-xs font-bold px-3 py-1.5 rounded-full mb-4 border border-red-200" data-id="element-461">
            ⚡ OFERTA LIMITADA — PRECIO ESPECIAL ACTIVO SOLO MIENTRAS DURE EL
            STOCK
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground" data-id="element-462">
            Elige Tu Pack y Empieza Hoy
          </h2>
          <p className="mt-3 text-muted-foreground text-lg max-w-xl mx-auto" data-id="element-463">
            El 78% de nuestras clientas eligen el pack de 3 cajas para
            resultados óptimos y duraderos.
          </p>
        </div>

        {/* Countdown above cards */}
        <div className="flex flex-col items-center gap-2 mb-8" data-id="element-464">
          <p className="text-sm font-semibold text-foreground" data-id="element-465">
            ⏰ La oferta termina en:
          </p>
          <div className="flex items-center gap-2" data-id="element-466">
            <div className="bg-foreground text-primary-foreground rounded-lg px-3 py-1.5 text-center min-w-[48px]" data-id="element-467">
              <span className="text-xl font-bold tabular-nums" data-id="element-468">{hh}</span>
              <p className="text-xs opacity-70" data-id="element-469">h</p>
            </div>
            <span className="text-foreground font-bold text-xl" data-id="element-470">:</span>
            <div className="bg-foreground text-primary-foreground rounded-lg px-3 py-1.5 text-center min-w-[48px]" data-id="element-471">
              <span className="text-xl font-bold tabular-nums" data-id="element-472">{mm}</span>
              <p className="text-xs opacity-70" data-id="element-473">min</p>
            </div>
            <span className="text-foreground font-bold text-xl" data-id="element-474">:</span>
            <div className="bg-foreground text-primary-foreground rounded-lg px-3 py-1.5 text-center min-w-[48px]" data-id="element-475">
              <span className="text-xl font-bold tabular-nums" data-id="element-476">{ss}</span>
              <p className="text-xs opacity-70" data-id="element-477">seg</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground" data-id="element-478">
            ⚠️ Precio especial activo solo mientras dure el stock
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start" data-id="element-479">
          <PricingCard units="1 Trimora · 7 Ampollas" quantity={1} price="34,95€" originalPrice="60€" savings="25€" savingsPct="42%" perUnit="34,95€/Trimora" perDay="1,66€" checkoutUrl="https://glowliftessencemx.com/cart/52008958296340:1" data-id="element-480" />
          <PricingCard units="2 Trimora · 14 Ampollas" quantity={2} price="59,95€" originalPrice="120€" savings="60€" savingsPct="50%" perUnit="29,97€/Trimora" perDay="1,00€" checkoutUrl="https://glowliftessencemx.com/cart/52008958329108:1" data-id="element-481" />
          <PricingCard units="3 Trimora · 21 Ampollas" quantity={3} price="79,95€" originalPrice="180€" savings="100€" savingsPct="56%" perUnit="26,65€/Trimora" perDay="0,63€" checkoutUrl="https://glowliftessencemx.com/cart/52008958361876:1" badge="🏆 Más Popular — Mejor Resultado" badgeStyle="bg-green-600 text-white" highlighted={true} doctorBadge={true} popular={true} data-id="element-482" />
          <PricingCard units="6 Trimora · 42 Ampollas" quantity={6} price="129,95€" originalPrice="360€" savings="230€" savingsPct="64%" perUnit="21,65€/Trimora" perDay="0,36€" checkoutUrl="https://glowliftessencemx.com/cart/52008958394644:1" badge="-64% OFF · Mejor Valor" badgeStyle="bg-foreground text-primary-foreground" data-id="element-483" />
        </div>

        {/* Trust row */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto" data-id="element-484">
          <div className="flex flex-col items-center gap-1 text-center" data-id="element-485">
            <span className="text-2xl" data-id="element-486">🚚</span>
            <p className="text-xs font-semibold text-foreground" data-id="element-487">
              Envío Gratis
            </p>
            <p className="text-xs text-muted-foreground" data-id="element-488">A toda España</p>
          </div>
          <div className="flex flex-col items-center gap-1 text-center" data-id="element-489">
            <span className="text-2xl" data-id="element-490">🛡️</span>
            <p className="text-xs font-semibold text-foreground" data-id="element-491">
              Garantía 90 Días
            </p>
            <p className="text-xs text-muted-foreground" data-id="element-492">Devolución total</p>
          </div>
          <div className="flex flex-col items-center gap-1 text-center" data-id="element-493">
            <span className="text-2xl" data-id="element-494">🔒</span>
            <p className="text-xs font-semibold text-foreground" data-id="element-495">Pago Seguro</p>
            <p className="text-xs text-muted-foreground" data-id="element-496">SSL 256-bit</p>
          </div>
          <div className="flex flex-col items-center gap-1 text-center" data-id="element-497">
            <span className="text-2xl" data-id="element-498">⭐</span>
            <p className="text-xs font-semibold text-foreground" data-id="element-499">
              4.8/5 Estrellas
            </p>
            <p className="text-xs text-muted-foreground" data-id="element-500">+10.000 reseñas</p>
          </div>
        </div>

        {/* Payment icons */}
        <div className="mt-6 flex flex-col items-center gap-3" data-id="element-501">
          <p className="text-sm text-muted-foreground font-medium" data-id="element-502">
            Métodos de pago aceptados
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-503">
            <span className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-bold text-muted-foreground" data-id="element-504">
              💳 Visa
            </span>
            <span className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-bold text-muted-foreground" data-id="element-505">
              💳 Mastercard
            </span>
            <span className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-bold text-muted-foreground" data-id="element-506">
              🅿️ PayPal
            </span>
            <span className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-bold text-muted-foreground" data-id="element-507">
              📱 Bizum
            </span>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-1" data-id="element-508">
            Todos los precios incluyen IVA. Envío gratuito a toda España.
          </p>
        </div>
      </div>
    </section>;
}